import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getScoreColor, verdictColor } from './scoring-utils';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Textarea } from '~/components/ui/textarea';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

type OverrideMode = 'assessment' | 'eligibility' | 'nice_to_have' | 'expectations';

interface ScoreOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionScoreId: string;
  questionText: string;
  currentValue: number | string;
  mode: OverrideMode;
  orgId: string;
  deptId: string;
  jobId: string;
  applicationId: string;
}

const SCORE_OPTIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
const MIN_REASON_LENGTH = 10;

function getVerdictOptions(mode: OverrideMode): string[] {
  if (mode === 'expectations') return ['pass', 'fail'];
  return ['pass', 'fail', 'unsure'];
}

export function ScoreOverrideDialog({
  open,
  onOpenChange,
  questionScoreId,
  questionText,
  currentValue,
  mode,
  orgId,
  deptId,
  jobId,
  applicationId
}: ScoreOverrideDialogProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedValue, setSelectedValue] = useState<string>(String(currentValue));
  const [reason, setReason] = useState('');

  const isAssessment = mode === 'assessment';
  const hasChanged = String(currentValue) !== selectedValue;
  const reasonValid = reason.trim().length >= MIN_REASON_LENGTH;
  const canSave = hasChanged && reasonValid;

  const mutation = useMutation({
    mutationFn: async () => {
      const json = isAssessment
        ? { score: Number(selectedValue), reason: reason.trim() }
        : { verdict: selectedValue as 'pass' | 'fail' | 'unsure', reason: reason.trim() };

      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications[':applicationId']['question-scores'][':questionScoreId'].$patch({
        param: { orgId, deptId, jobId, applicationId, questionScoreId },
        json
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['scoring', orgId, deptId, jobId, applicationId]
      });
      onOpenChange(false);
      setReason('');
    },
    onError: () => {
      toast.error(i18n.t('scoring.overrideDialog.error'));
    }
  });

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      setSelectedValue(String(currentValue));
      setReason('');
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(
              isAssessment ? 'scoring.overrideDialog.title' : 'scoring.overrideDialog.titleVerdict'
            )}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {questionText}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">{t('scoring.overrideDialog.newValue')}</label>
            {isAssessment ? (
              <Select
                value={selectedValue}
                onValueChange={(val) => {
                  if (val) setSelectedValue(val);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue>
                    <span className={`font-semibold ${getScoreColor(Number(selectedValue))}`}>
                      {Number(selectedValue) % 1 === 0
                        ? selectedValue
                        : Number(selectedValue).toFixed(1)}
                    </span>
                    <span className="text-muted-foreground"> / 5</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {SCORE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      <span className={`font-semibold ${getScoreColor(n)}`}>
                        {n % 1 === 0 ? n : n.toFixed(1)}
                      </span>{' '}
                      / 5
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={selectedValue}
                onValueChange={(val) => {
                  if (val) setSelectedValue(val);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue>
                    <span className={`font-medium ${verdictColor(selectedValue)}`}>
                      {t(`scoring.verdicts.${selectedValue}` as 'scoring.verdicts.pass')}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {getVerdictOptions(mode).map((v) => (
                    <SelectItem key={v} value={v}>
                      <span className={`font-medium ${verdictColor(v)}`}>
                        {t(`scoring.verdicts.${v}` as 'scoring.verdicts.pass')}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">{t('scoring.overrideDialog.reason')}</label>
            <Textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder={t('scoring.overrideDialog.reasonPlaceholder')}
              rows={3}
            />
            {reason.length > 0 && !reasonValid && (
              <p className="text-xs text-muted-foreground">
                {t('scoring.overrideDialog.reasonMinLength', { min: MIN_REASON_LENGTH })}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            {t('scoring.overrideDialog.cancel')}
          </Button>
          <Button
            disabled={!canSave || mutation.isPending}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {t('scoring.overrideDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
